import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Check, ChevronRight, Gauge, MapPin, Pencil, Plus, Search, Store, Trash2, Truck, X } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { Link, useOutletContext } from 'react-router'
import { useIdiomas } from '../api/idiomas'
import { EditorDeTraducciones } from '../motor/campos/Traducciones'

import { consulta, ErrorDeLaApi, pedir } from '../api/nucleo'
import type { Sitio } from '../api/tipos'
import { Aviso, Boton, BotonDeIcono, Casilla, claseDeEntrada, Esqueleto, EstadoVacio, Modal, Pastilla, Selector, Tarjeta, Titulo } from '../componentes/ui'
import { coberturaEnLlano, errorDeGuardado, ETIQUETAS_DE_CONSUMO, mesEnLlano, type Pantalla, precioEnLlano, reordenar, revisarTarifaPorDistancia, tarifaEnLlano, type TipoDeZona, type UsoDeMapas, usoDeMapasEnLlano, type ZonaEnPantalla } from './entregasEnLlano'
import type { Contexto } from './Modulo'
import { dinero } from './tiendaEnLlano'

/**
 * «Envíos y entregas» de una tienda (CLAUDE.md §23): las zonas de envío armadas con lugares
 * oficiales —un departamento entero, unas provincias, unos distritos sueltos o el país
 * entero—, «otros países», el recojo en tienda y, si la tienda quiere, el envío por distancia
 * con la clave de Google Maps de Wixy (lo que gasta lo cuenta su plan). Todo lo que se guarda
 * pasa por la API de siempre (`/api/zonas-de-envio`), y lo que vale lo decide el núcleo: aquí
 * solo se pinta y se pregunta.
 */

export const Entregas = () => {
  const { espacio, permisos, sitio } = useOutletContext<Contexto>()
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  if (permisos.cargando) return <Esqueleto />
  // Las zonas de envío las lee y escribe quien tiene `tienda.envios` (la colección `zonas-de-envio`).
  if (!permisos.tiene('tienda.envios')) {
    return (
      <>
        <Titulo rotulo={sitio.nombre}>Envíos y entregas</Titulo>
        <Aviso tono="info">Los envíos de la tienda los configura quien administra la cuenta. Si hace falta cambiar algo, pídeselo.</Aviso>
      </>
    )
  }
  if (!(sitio.modulos ?? []).includes('productos')) {
    return (
      <>
        <Titulo rotulo={sitio.nombre}>Envíos y entregas</Titulo>
        <Aviso tono="info">Esta web no tiene tienda. Si la necesita, hay que añadirle el módulo de productos.</Aviso>
      </>
    )
  }
  return <PantallaDeEntregas espacio={espacio} key={sitio.id} sitio={sitio} />
}

const PantallaDeEntregas = ({ espacio, sitio }: { espacio: number; sitio: Sitio }) => {
  const clienteDeConsultas = useQueryClient()
  const clave = ['entregas', sitio.id]
  const { data, error, isPending } = useQuery({ queryKey: clave, queryFn: () => pedir<Pantalla>(`/zonas-de-envio/pantalla${consulta({ sitio: sitio.id })}`) })
  const recargar = () => void clienteDeConsultas.invalidateQueries({ queryKey: clave })
  const [editando, setEditando] = useState<null | { tipo: TipoDeZona; zona: null | ZonaEnPantalla }>(null)
  const [borrando, setBorrando] = useState<null | ZonaEnPantalla>(null)
  const [aviso, setAviso] = useState<null | { texto: string; tono: 'error' | 'ok' }>(null)
  const [ocupado, setOcupado] = useState<null | string>(null)

  const hacer = async (que: string, accion: () => Promise<unknown>, bien?: string) => {
    setOcupado(que)
    setAviso(null)
    try {
      await accion()
      if (bien) setAviso({ texto: bien, tono: 'ok' })
      recargar()
      return true
    } catch (e) {
      setAviso({ texto: errorDeGuardado(e).mensaje, tono: 'error' })
      return false
    } finally {
      setOcupado(null)
    }
  }

  if (isPending)
    return (
      <>
        <Titulo rotulo={sitio.nombre}>Envíos y entregas</Titulo>
        <Tarjeta sinRelleno>
          <Esqueleto filas={6} />
        </Tarjeta>
      </>
    )
  if (error || !data) return <Aviso tono="error">{error instanceof ErrorDeLaApi ? error.message : 'No hemos podido leer los envíos de esta web. Vuelve a intentarlo en un momento.'}</Aviso>

  const zonas = data.zonas.filter((z) => z.tipo === 'zona' || z.tipo === 'extranjero' || z.tipo === 'a-mano')
  const recojos = data.zonas.filter((z) => z.tipo === 'recojo')
  const porDistancia = data.zonas.find((z) => z.tipo === 'distancia') ?? null
  const aMano = zonas.filter((z) => z.tipo === 'a-mano' && z.activa)
  const mover = (z: ZonaEnPantalla, sentido: -1 | 1) =>
    void hacer(`mover-${z.id}`, async () => {
      for (const cambio of reordenar(data.zonas, z.id, sentido)) await pedir(`/zonas-de-envio/${cambio.id}`, { metodo: 'PATCH', cuerpo: { orden: cambio.orden } })
    })
  const activar = (z: ZonaEnPantalla) => void hacer(`activar-${z.id}`, () => pedir(`/zonas-de-envio/${z.id}`, { metodo: 'PATCH', cuerpo: { activa: !z.activa } }))

  return (
    <>
      <Titulo
        accion={
          <Link to={`/e/${espacio}/s/${sitio.id}/pedidos`}>
            <Boton>Ver los pedidos</Boton>
          </Link>
        }
        descripcion="Cómo llega cada pedido: a qué lugares envías y cuánto cuesta, si se puede recoger en la tienda y, si quieres, el precio por kilómetros desde tu local con Google Maps."
        rotulo={sitio.nombre}
      >
        Envíos y entregas
      </Titulo>

      <div className="flex flex-col gap-5">
        {!data.vendeEnLinea && (
          <Aviso tono="info">
            Tu tienda está en modo catálogo: estas formas de entrega se usarán cuando vendas en línea (Ajustes → Tienda → «¿Cómo vendes?»).
          </Aviso>
        )}
        {data.zonas.filter((z) => z.activa).length === 0 && (
          <Aviso tono="aviso">Todavía no tienes ninguna forma de entrega activa: el carrito no pregunta a dónde va el pedido y el envío no se cobra. Añade una zona o un punto de recojo.</Aviso>
        )}
        {aMano.length > 0 && (
          <Aviso tono="info">
            Tienes {aMano.length === 1 ? 'una zona escrita a mano' : `${aMano.length} zonas escritas a mano`} de antes: siguen cobrando igual, y el comprador las elige de una lista. Para que el envío se calcule solo según la dirección, crea zonas con sus lugares y desactiva las de antes.
          </Aviso>
        )}
        {aviso && <Aviso tono={aviso.tono}>{aviso.texto}</Aviso>}

        <Tarjeta>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold">
              <Truck aria-hidden className="size-4 text-violeta" /> Zonas de envío
            </h2>
            <div className="flex flex-wrap gap-2">
              <Boton icono={<Plus />} onClick={() => setEditando({ tipo: 'zona', zona: null })} pequeno tipo="primario">
                Añadir una zona
              </Boton>
              {!zonas.some((z) => z.tipo === 'extranjero') && (
                <Boton icono={<Plus />} onClick={() => setEditando({ tipo: 'extranjero', zona: null })} pequeno>
                  Envío a otros países
                </Boton>
              )}
            </div>
          </div>
          <p className="mb-4 text-[13px] text-tenue">
            A cada dirección le toca <strong>la zona más concreta que la contenga</strong>: un distrito suelto gana a su provincia, la provincia a su departamento y el departamento al país entero. A igual nivel, la que va antes en esta lista.
          </p>
          {zonas.length === 0 ? (
            <EstadoVacio
              descripcion="Por ejemplo: «Lima Metropolitana» con sus distritos, S/ 10 y «24 a 48 horas»; y «Resto del Perú» con el país entero, S/ 25."
              icono={<Truck />}
              titulo="Todavía no hay zonas de envío"
            />
          ) : (
            <ul className="flex flex-col divide-y divide-linea">
              {zonas.map((z) => (
                <FilaDeZona
                  activar={() => activar(z)}
                  borrar={() => setBorrando(z)}
                  editar={() => setEditando({ tipo: z.tipo, zona: z })}
                  key={z.id}
                  moneda={data.moneda}
                  mover={(sentido) => mover(z, sentido)}
                  ocupado={ocupado !== null}
                  paises={data.paises}
                  zona={z}
                />
              ))}
            </ul>
          )}
        </Tarjeta>

        <Tarjeta>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-display text-base font-semibold">
              <Store aria-hidden className="size-4 text-violeta" /> Recojo en tienda
            </h2>
            <Boton icono={<Plus />} onClick={() => setEditando({ tipo: 'recojo', zona: null })} pequeno>
              Añadir un punto de recojo
            </Boton>
          </div>
          <p className="mb-4 text-[13px] text-tenue">El comprador lo recoge en tu local, gratis. Puedes tener varios puntos, cada uno con su dirección y su horario.</p>
          {recojos.length === 0 ? (
            <p className="text-sm text-tenue">Sin puntos de recojo: el carrito solo ofrece el envío.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-linea">
              {recojos.map((z) => (
                <FilaDeZona
                  activar={() => activar(z)}
                  borrar={() => setBorrando(z)}
                  editar={() => setEditando({ tipo: 'recojo', zona: z })}
                  key={z.id}
                  moneda={data.moneda}
                  mover={(sentido) => mover(z, sentido)}
                  ocupado={ocupado !== null}
                  paises={data.paises}
                  zona={z}
                />
              ))}
            </ul>
          )}
        </Tarjeta>

        <GoogleMaps alCambiar={recargar} espacio={espacio} googleDisponible={data.googleDisponible} moneda={data.moneda} paises={data.paises} sitio={sitio.id} zona={porDistancia} zonasPorLugares={zonas.filter((z) => z.tipo === 'zona').length} />

        <UsoDeMapas sitio={sitio.id} />

        <ProbarDireccion moneda={data.moneda} paises={data.paises} paisPorOmision={data.zonas.find((z) => z.pais)?.pais ?? 'PE'} sitio={sitio.id} />

        <p className="text-[13px] text-tenue">
          Cada pedido guarda la dirección, la zona, el plazo y el precio del envío tal como eran al pedir: cambiar una zona no cambia los pedidos ya hechos.
        </p>
      </div>

      {editando && (
        <EditorDeZona
          alGuardar={() => {
            setEditando(null)
            recargar()
          }}
          cerrar={() => setEditando(null)}
          espacio={espacio}
          moneda={data.moneda}
          orden={data.zonas.reduce((m, z) => Math.max(m, z.orden), 0) + 10}
          paises={data.paises}
          paisPorOmision={data.zonas.find((z) => z.pais)?.pais ?? 'PE'}
          sitio={sitio.id}
          tipo={editando.tipo}
          zona={editando.zona}
        />
      )}

      {borrando && (
        <Modal
          ancho="max-w-md"
          cerrar={() => setBorrando(null)}
          pie={
            <>
              <Boton onClick={() => setBorrando(null)}>Cancelar</Boton>
              <Boton
                cargando={ocupado === `borrar-${borrando.id}`}
                icono={<Trash2 />}
                onClick={() => void hacer(`borrar-${borrando.id}`, () => pedir(`/zonas-de-envio/${borrando.id}`, { metodo: 'DELETE' }), `«${borrando.nombre}» quitada.`).then(() => setBorrando(null))}
                tipo="peligro"
              >
                Quitar
              </Boton>
            </>
          }
          titulo={`¿Quitar «${borrando.nombre}»?`}
        >
          <p className="text-sm">El carrito dejará de ofrecerla desde ya. Los pedidos que se hicieron con ella no cambian. Si solo quieres pausarla, desactívala en vez de quitarla.</p>
        </Modal>
      )}
    </>
  )
}

const FilaDeZona = ({
  zona,
  moneda,
  paises,
  editar,
  borrar,
  activar,
  mover,
  ocupado,
}: {
  activar: () => void
  borrar: () => void
  editar: () => void
  moneda: string
  mover: (sentido: -1 | 1) => void
  ocupado: boolean
  paises: Pantalla['paises']
  zona: ZonaEnPantalla
}) => (
  <li className="flex flex-wrap items-center justify-between gap-3 py-3">
    <div className="min-w-0 flex-1">
      <p className="flex flex-wrap items-center gap-2 font-medium">
        {zona.nombre}
        {!zona.activa && <Pastilla clave="sin-publicar">Desactivada</Pastilla>}
        {zona.tipo === 'a-mano' && <Pastilla clave="neutra">Escrita a mano</Pastilla>}
        {zona.tipo === 'extranjero' && <Pastilla clave="violeta">Otros países</Pastilla>}
      </p>
      <p className="text-[13px] text-tenue">
        {zona.tipo === 'recojo' ? [zona.direccion, zona.horario].filter(Boolean).join(' · ') : coberturaEnLlano(zona, paises)}
      </p>
      <p className="text-[13px] text-tinta-2">
        {zona.tipo === 'recojo' ? 'Gratis' : precioEnLlano(zona.precio, moneda)}
        {zona.plazo ? ` · ${zona.plazo}` : ''}
        {typeof zona.gratisDesde === 'number' && zona.gratisDesde > 0 ? ` · gratis desde ${precioEnLlano(zona.gratisDesde, moneda)}` : ''}
      </p>
    </div>
    <div className="flex shrink-0 items-center gap-1">
      <label className="mr-2 flex items-center gap-1.5 text-[13px]">
        <Casilla disabled={ocupado} etiqueta={`Activa: ${zona.nombre}`} marcada={zona.activa} onChange={activar} />
        Activa
      </label>
      <BotonDeIcono disabled={ocupado} etiqueta="Subir" onClick={() => mover(-1)} pequeno>
        <ArrowUp />
      </BotonDeIcono>
      <BotonDeIcono disabled={ocupado} etiqueta="Bajar" onClick={() => mover(1)} pequeno>
        <ArrowDown />
      </BotonDeIcono>
      <BotonDeIcono etiqueta={`Editar ${zona.nombre}`} onClick={editar} pequeno>
        <Pencil />
      </BotonDeIcono>
      <BotonDeIcono etiqueta={`Quitar ${zona.nombre}`} onClick={borrar} pequeno tipo="peligro">
        <Trash2 />
      </BotonDeIcono>
    </div>
  </li>
)

const CampoDeTexto = ({
  etiqueta,
  valor,
  cambiar,
  error,
  ayuda,
  placeholder,
  tipo = 'text',
  autofoco = false,
}: {
  autofoco?: boolean
  ayuda?: string
  cambiar: (v: string) => void
  error?: null | string
  etiqueta: string
  placeholder?: string
  tipo?: string
  valor: string
}) => {
  const id = useId()
  return (
    <label className="flex flex-col gap-1.5" htmlFor={id}>
      <span className="text-[13px] font-medium text-tinta">{etiqueta}</span>
      <input
        aria-describedby={error ? `${id}-e` : ayuda ? `${id}-a` : undefined}
        aria-invalid={error ? true : undefined}
        className={claseDeEntrada(error ?? undefined)}
        data-autofocus={autofoco ? true : undefined}
        id={id}
        inputMode={tipo === 'decimal' ? 'decimal' : undefined}
        onChange={(e) => cambiar(e.target.value)}
        placeholder={placeholder}
        type={tipo === 'decimal' ? 'text' : tipo}
        value={valor}
      />
      {ayuda && !error && (
        <span className="text-xs text-tenue" id={`${id}-a`}>
          {ayuda}
        </span>
      )}
      {error && (
        <span className="text-xs text-error" id={`${id}-e`} role="alert">
          {error}
        </span>
      )}
    </label>
  )
}

const numeroDe = (texto: string): null | number => (texto.trim() === '' ? null : Number(texto.replace(',', '.')))
const textoDe = (n: null | number | undefined): string => (typeof n === 'number' ? String(n) : '')

const TITULOS: Record<TipoDeZona, string> = {
  zona: 'Zona de envío',
  extranjero: 'Envío a otros países',
  recojo: 'Punto de recojo',
  'a-mano': 'Zona escrita a mano',
  distancia: 'Envío por distancia',
}

const EditorDeZona = ({
  tipo,
  zona,
  espacio,
  sitio,
  moneda,
  paises,
  paisPorOmision,
  orden,
  cerrar,
  alGuardar,
}: {
  alGuardar: () => void
  cerrar: () => void
  espacio: number
  moneda: string
  orden: number
  paises: Pantalla['paises']
  paisPorOmision: string
  sitio: number
  tipo: TipoDeZona
  zona: null | ZonaEnPantalla
}) => {
  const [nombre, setNombre] = useState(zona?.nombre ?? (tipo === 'extranjero' ? 'Otros países' : tipo === 'recojo' ? 'Recojo en tienda' : ''))
  const [pais, setPais] = useState(zona?.pais ?? paisPorOmision)
  const [lugares, setLugares] = useState<Array<{ codigo: string; camino: string }>>(zona?.lugaresDetalle ?? [])
  const [precio, setPrecio] = useState(textoDe(zona?.precio))
  const [plazo, setPlazo] = useState(zona?.plazo ?? '')
  const [gratisDesde, setGratisDesde] = useState(textoDe(zona?.gratisDesde))
  const [etiqueta, setEtiqueta] = useState(zona?.etiqueta ?? '')
  const [direccion, setDireccion] = useState(zona?.direccion ?? '')
  const [horario, setHorario] = useState(zona?.horario ?? '')
  const [activa, setActiva] = useState(zona?.activa ?? true)
  const [traducciones, setTraducciones] = useState(zona?.traducciones ?? null)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<null | { mensaje: string; campo: null | string }>(null)
  const errorEn = (campo: string) => (error?.campo === campo ? error.mensaje : null)

  const guardar = async () => {
    setGuardando(true)
    setError(null)
    const datos: Record<string, unknown> = { nombre, tipo, plazo, activa, traducciones }
    if (tipo === 'zona') Object.assign(datos, { pais, lugares: lugares.map((l) => l.codigo), precio: numeroDe(precio), gratisDesde: numeroDe(gratisDesde) })
    if (tipo === 'extranjero' || tipo === 'a-mano') Object.assign(datos, { precio: numeroDe(precio), gratisDesde: numeroDe(gratisDesde) })
    if (tipo === 'a-mano') datos.etiqueta = etiqueta
    if (tipo === 'recojo') Object.assign(datos, { direccion, horario })
    try {
      if (zona) await pedir(`/zonas-de-envio/${zona.id}`, { metodo: 'PATCH', cuerpo: datos })
      else await pedir('/zonas-de-envio', { metodo: 'POST', cuerpo: { ...datos, cliente: espacio, sitio, orden } })
      alGuardar()
    } catch (e) {
      setError(errorDeGuardado(e))
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Modal
      ancho="max-w-2xl"
      cerrar={cerrar}
      pie={
        <>
          <Boton onClick={cerrar}>Cancelar</Boton>
          <Boton cargando={guardando} onClick={() => void guardar()} tipo="primario">
            Guardar
          </Boton>
        </>
      }
      titulo={zona ? `Editar «${zona.nombre}»` : `Nueva: ${TITULOS[tipo].toLowerCase()}`}
    >
      <div className="flex flex-col gap-4">
        {error && !error.campo && <Aviso tono="error">{error.mensaje}</Aviso>}
        <CampoDeTexto
          autofoco
          ayuda="Es lo que ve el comprador al elegir."
          cambiar={setNombre}
          error={errorEn('nombre')}
          etiqueta="Nombre"
          placeholder={tipo === 'recojo' ? 'Recojo en Miraflores' : 'Lima Metropolitana'}
          valor={nombre}
        />

        {tipo === 'zona' && (
          <>
            <label className="flex flex-col gap-1.5">
              <span className="text-[13px] font-medium">País</span>
              <Selector
                onChange={(e) => {
                  setPais(e.target.value)
                  setLugares([])
                }}
                value={pais}
              >
                {paises.map((p) => (
                  <option key={p.codigo} value={p.codigo}>
                    {p.nombre}
                  </option>
                ))}
              </Selector>
              {errorEn('pais') && (
                <span className="text-xs text-error" role="alert">
                  {errorEn('pais')}
                </span>
              )}
            </label>
            <SelectorDeLugares cambiar={setLugares} error={errorEn('lugares')} pais={pais} paisNombre={paises.find((p) => p.codigo === pais)?.nombre ?? pais} valor={lugares} />
          </>
        )}

        {tipo === 'recojo' && (
          <>
            <CampoDeTexto ayuda="La dirección que lee el comprador: calle, número y distrito." cambiar={setDireccion} error={errorEn('direccion')} etiqueta="Dirección" placeholder="Av. Benavides 500, Miraflores" valor={direccion} />
            <CampoDeTexto cambiar={setHorario} error={errorEn('horario')} etiqueta="Horario" placeholder="Lunes a viernes, de 9:00 a 18:00" valor={horario} />
          </>
        )}

        {tipo === 'a-mano' && (
          <>
            <Aviso tono="info">Una zona escrita a mano no sabe qué direcciones cubre: el comprador la elige por su nombre. Para que se calcule sola, crea una zona con sus lugares y desactiva esta.</Aviso>
            <CampoDeTexto cambiar={setEtiqueta} error={errorEn('etiqueta')} etiqueta="Lo que se le dice" placeholder="Envío a Lima, 2 a 3 días" valor={etiqueta} />
          </>
        )}

        {tipo !== 'recojo' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <CampoDeTexto ayuda={`En ${moneda}. 0 si es gratis.`} cambiar={setPrecio} error={errorEn('precio')} etiqueta="Precio del envío" placeholder="10" tipo="decimal" valor={precio} />
            <CampoDeTexto ayuda="Si el carrito llega a este importe, el envío es gratis. Vacío: nunca." cambiar={setGratisDesde} error={errorEn('gratisDesde')} etiqueta="Envío gratis desde (opcional)" placeholder="200" tipo="decimal" valor={gratisDesde} />
          </div>
        )}
        <CampoDeTexto cambiar={setPlazo} error={errorEn('plazo')} etiqueta={tipo === 'recojo' ? 'Cuándo está listo (opcional)' : 'Plazo de entrega'} placeholder={tipo === 'recojo' ? 'Al día siguiente' : '24 a 48 horas'} valor={plazo} />
        <TraduccionesDeZona cambiar={setTraducciones} datos={{ nombre, plazo, etiqueta: tipo === 'a-mano' ? etiqueta : null, horario: tipo === 'recojo' ? horario : null }} sitio={sitio} valor={traducciones} />
        <label className="flex items-center gap-2 text-sm">
          <Casilla marcada={activa} onChange={() => setActiva(!activa)} />
          Activa: el carrito la ofrece
        </label>
      </div>
    </Modal>
  )
}

type LugarDeLaLista = { codigo: string; nombre: string; hoja: boolean }
type Nivel = { niveles: string[]; nivel: number; nombreDelNivel: null | string; lugares: LugarDeLaLista[] }
type Encontrado = { codigo: string; nombre: string; camino: string; nombreDelNivel: null | string; hoja: boolean }

/**
 * Elegir los lugares de una zona: **buscar** por nombre («Miraflores» → «Lima › Lima ›
 * Miraflores») o **recorrer** el país por niveles, y añadir lo que haga falta, de cualquier
 * nivel. Lo que ya está dentro de otro lugar de la zona lo quita el núcleo al guardar.
 */
const SelectorDeLugares = ({
  pais,
  paisNombre,
  valor,
  cambiar,
  error,
}: {
  cambiar: (lugares: Array<{ codigo: string; camino: string }>) => void
  error: null | string
  pais: string
  paisNombre: string
  valor: Array<{ codigo: string; camino: string }>
}) => {
  const idBusqueda = useId()
  const [texto, setTexto] = useState('')
  const [encontrados, setEncontrados] = useState<Encontrado[]>([])
  const [camino, setCamino] = useState<Array<{ codigo: string; nombre: string }>>([])
  const padre = camino.at(-1)?.codigo ?? null
  const { data: nivel, isPending } = useQuery({
    queryKey: ['lugares', pais, padre],
    queryFn: () => pedir<Nivel>(`/zonas-de-envio/lugares${consulta({ pais, padre: padre ?? '' })}`),
    staleTime: Infinity,
  })

  useEffect(() => {
    setCamino([])
    setTexto('')
  }, [pais])

  useEffect(() => {
    const q = texto.trim()
    if (q.length < 2) {
      setEncontrados([])
      return
    }
    let vigente = true
    const espera = window.setTimeout(() => {
      void pedir<{ lugares: Encontrado[] }>(`/zonas-de-envio/buscar${consulta({ pais, q })}`)
        .then((r) => vigente && setEncontrados(r.lugares))
        .catch(() => vigente && setEncontrados([]))
    }, 250)
    return () => {
      vigente = false
      window.clearTimeout(espera)
    }
  }, [texto, pais])

  const elegidos = new Set(valor.map((l) => l.codigo))
  const anadir = (codigo: string, caminoDelLugar: string) => {
    if (elegidos.has(codigo)) return
    cambiar([...valor, { codigo, camino: caminoDelLugar }])
  }
  const caminoDe = (nombre: string) => [...camino.map((c) => c.nombre), nombre].join(' › ')
  const sinDivisiones = !isPending && nivel && nivel.lugares.length === 0 && camino.length === 0

  return (
    <div className="flex flex-col gap-3 rounded-caja border border-linea bg-papel-2/40 p-3">
      <div>
        <p className="text-[13px] font-medium">Lugares que cubre</p>
        <p className="text-xs text-tenue">Sin ninguno, la zona cubre todo {paisNombre} (útil para «Resto del país»). Puedes mezclar niveles: un departamento entero y unos distritos de otro.</p>
      </div>

      {valor.length > 0 && (
        <ul aria-label="Lugares elegidos" className="flex flex-wrap gap-1.5">
          {valor.map((l) => (
            <li className="inline-flex items-center gap-1 rounded-pastilla border border-violeta-borde bg-violeta-suave py-0.5 pr-1 pl-2.5 text-xs text-violeta" key={l.codigo}>
              {l.camino}
              <button aria-label={`Quitar ${l.camino}`} className="rounded-full p-0.5 hover:bg-papel" onClick={() => cambiar(valor.filter((x) => x.codigo !== l.codigo))} type="button">
                <X aria-hidden className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="text-xs text-error" role="alert">
          {error}
        </p>
      )}

      {sinDivisiones ? (
        <p className="text-xs text-tenue">Todavía no tenemos las divisiones de {paisNombre}: la zona cubre el país entero.</p>
      ) : (
        <>
          <label className="relative block" htmlFor={idBusqueda}>
            <span className="sr-only">Buscar un lugar por su nombre</span>
            <Search aria-hidden className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-tenue" />
            <input className={`${claseDeEntrada()} pl-8`} id={idBusqueda} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar: Miraflores, Arequipa…" type="search" value={texto} />
          </label>
          {texto.trim().length >= 2 ? (
            encontrados.length === 0 ? (
              <p className="text-xs text-tenue">No hay ningún lugar con ese nombre en {paisNombre}.</p>
            ) : (
              <ul className="flex max-h-56 flex-col divide-y divide-linea overflow-y-auto rounded-caja border border-linea bg-papel">
                {encontrados.map((l) => (
                  <li className="flex items-center justify-between gap-2 px-3 py-2 text-sm" key={l.codigo}>
                    <span className="min-w-0">
                      <span className="block truncate">{l.camino}</span>
                      {l.nombreDelNivel && <span className="text-xs text-tenue">{l.nombreDelNivel}</span>}
                    </span>
                    <Boton disabled={elegidos.has(l.codigo)} icono={elegidos.has(l.codigo) ? <Check /> : <Plus />} onClick={() => anadir(l.codigo, l.camino)} pequeno tipo="discreto">
                      {elegidos.has(l.codigo) ? 'Añadido' : 'Añadir'}
                    </Boton>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="rounded-caja border border-linea bg-papel">
              <nav aria-label="Dónde estás" className="flex flex-wrap items-center gap-1 border-b border-linea px-3 py-2 text-xs">
                <button className="text-violeta hover:underline" onClick={() => setCamino([])} type="button">
                  {paisNombre}
                </button>
                {camino.map((c, i) => (
                  <span className="inline-flex items-center gap-1" key={c.codigo}>
                    <ChevronRight aria-hidden className="size-3 text-tenue" />
                    <button className="text-violeta hover:underline" onClick={() => setCamino(camino.slice(0, i + 1))} type="button">
                      {c.nombre}
                    </button>
                  </span>
                ))}
              </nav>
              {isPending || !nivel ? (
                <Esqueleto filas={3} />
              ) : (
                <ul className="flex max-h-56 flex-col divide-y divide-linea overflow-y-auto">
                  {nivel.lugares.map((l) => (
                    <li className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm" key={l.codigo}>
                      <span className="min-w-0 truncate">{l.nombre}</span>
                      <span className="flex shrink-0 items-center gap-1">
                        <Boton disabled={elegidos.has(l.codigo)} icono={elegidos.has(l.codigo) ? <Check /> : <Plus />} onClick={() => anadir(l.codigo, caminoDe(l.nombre))} pequeno tipo="discreto">
                          {elegidos.has(l.codigo) ? 'Añadido' : `Añadir ${nivel.nombreDelNivel?.toLowerCase() ?? ''}`.trim()}
                        </Boton>
                        {!l.hoja && (
                          <Boton
                            aria-label={`Ver ${nivel.niveles[nivel.nivel] ? `cada ${nivel.niveles[nivel.nivel]!.toLowerCase()}` : 'lo que hay dentro'} de ${l.nombre}`}
                            icono={<ChevronRight />}
                            onClick={() => setCamino([...camino, { codigo: l.codigo, nombre: l.nombre }])}
                            pequeno
                            tipo="discreto"
                          >
                            Abrir
                          </Boton>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/** «¿A qué zona va esta dirección?»: con el mismo cálculo que el carrito, sin hacer un pedido. */
const ProbarDireccion = ({ sitio, paises, moneda, paisPorOmision }: { moneda: string; paises: Pantalla['paises']; paisPorOmision: string; sitio: number }) => {
  const [pais, setPais] = useState(paisPorOmision)
  const [texto, setTexto] = useState('')
  const [encontrados, setEncontrados] = useState<Encontrado[]>([])
  const [resultado, setResultado] = useState<null | { ok: boolean; mensaje?: string; etiqueta?: string; tarifa?: number; camino?: string; gratisDesde?: null | number }>(null)

  useEffect(() => {
    const q = texto.trim()
    if (q.length < 2) {
      setEncontrados([])
      return
    }
    let vigente = true
    const espera = window.setTimeout(() => {
      void pedir<{ lugares: Encontrado[] }>(`/zonas-de-envio/buscar${consulta({ pais, q })}`)
        .then((r) => vigente && setEncontrados(r.lugares.slice(0, 8)))
        .catch(() => vigente && setEncontrados([]))
    }, 250)
    return () => {
      vigente = false
      window.clearTimeout(espera)
    }
  }, [texto, pais])

  const probar = (lugar: null | string) =>
    void pedir<NonNullable<typeof resultado>>(`/zonas-de-envio/resolver${consulta({ sitio, pais, lugar: lugar ?? '' })}`)
      .then(setResultado)
      .catch((e: Error) => setResultado({ ok: false, mensaje: e.message }))

  return (
    <Tarjeta>
      <h2 className="mb-1 flex items-center gap-2 font-display text-base font-semibold">
        <MapPin aria-hidden className="size-4 text-violeta" /> Probar una dirección
      </h2>
      <p className="mb-3 text-[13px] text-tenue">Elige un lugar y te decimos qué zona le toca y cuánto paga, como lo vería el comprador.</p>
      <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
        <Selector
          aria-label="País"
          onChange={(e) => {
            setPais(e.target.value)
            setResultado(null)
          }}
          value={pais}
        >
          {paises.map((p) => (
            <option key={p.codigo} value={p.codigo}>
              {p.nombre}
            </option>
          ))}
        </Selector>
        <input aria-label="Buscar un lugar" className={claseDeEntrada()} onChange={(e) => setTexto(e.target.value)} placeholder="Escribe un distrito o una ciudad" type="search" value={texto} />
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {encontrados.map((l) => (
          <Boton key={l.codigo} onClick={() => probar(l.codigo)} pequeno tipo="discreto">
            {l.camino}
          </Boton>
        ))}
        {texto.trim() === '' && (
          <Boton onClick={() => probar(null)} pequeno tipo="discreto">
            Probar el país sin elegir lugar
          </Boton>
        )}
      </div>
      {resultado && (
        <div className="mt-3">
          {resultado.ok ? (
            <Aviso tono="ok">
              {resultado.camino ? `${resultado.camino}: ` : ''}le toca «{resultado.etiqueta}», {resultado.tarifa === 0 ? 'gratis' : dinero(resultado.tarifa, moneda)}
              {typeof resultado.gratisDesde === 'number' ? ` (gratis desde ${dinero(resultado.gratisDesde, moneda)})` : ''}.
            </Aviso>
          ) : (
            <Aviso tono="aviso">{resultado.mensaje}</Aviso>
          )}
        </div>
      )}
    </Tarjeta>
  )
}


/**
 * El envío por distancia con Google Maps: opcional y por web, **con la clave de Wixy**. La
 * tienda no pega ninguna clave ni abre nada en Google Cloud: escribe la dirección de su local
 * (el núcleo la ubica), su precio base, su precio por kilómetro y hasta dónde llega. Lo que
 * gasta en Google lo cuenta su plan (la tarjeta «Uso de mapas este mes», debajo).
 */
const GoogleMaps = ({
  zona,
  sitio,
  espacio,
  moneda,
  paises,
  googleDisponible,
  zonasPorLugares,
  alCambiar,
}: {
  alCambiar: () => void
  espacio: number
  googleDisponible: boolean
  moneda: string
  paises: Pantalla['paises']
  sitio: number
  zona: null | ZonaEnPantalla
  zonasPorLugares: number
}) => {
  const [pais, setPais] = useState(zona?.pais ?? 'PE')
  const [direccion, setDireccion] = useState(zona?.origen?.direccion ?? '')
  const [precioBase, setPrecioBase] = useState(textoDe(zona?.precioBase))
  const [precioPorKm, setPrecioPorKm] = useState(textoDe(zona?.precioPorKm))
  const [radioMaximoKm, setRadioMaximoKm] = useState(textoDe(zona?.radioMaximoKm))
  const [masAlla, setMasAlla] = useState<'no-enviar' | 'zonas'>(zona?.masAlla ?? 'no-enviar')
  const [plazo, setPlazo] = useState(zona?.plazo ?? '')
  const [gratisDesde, setGratisDesde] = useState(textoDe(zona?.gratisDesde))
  const [activa, setActiva] = useState(zona?.activa ?? googleDisponible)
  const [traducciones, setTraducciones] = useState(zona?.traducciones ?? null)
  const [guardando, setGuardando] = useState(false)
  const [ubicando, setUbicando] = useState(false)
  const [mensaje, setMensaje] = useState<null | { texto: string; tono: 'aviso' | 'error' | 'ok' }>(null)
  const [error, setError] = useState<null | { mensaje: string; campo: null | string }>(null)
  const [quitando, setQuitando] = useState(false)

  const ubicada = Boolean(zona?.origen?.direccion && typeof zona.origen.lat === 'number' && typeof zona.origen.lng === 'number')
  const direccionCambiada = direccion.trim() !== (zona?.origen?.direccion ?? '')
  const listo = Boolean(zona && ubicada && typeof zona.precioBase === 'number' && typeof zona.radioMaximoKm === 'number' && zona.radioMaximoKm > 0)
  const errorEn = (campo: string) => (error?.campo === campo ? error.mensaje : null)

  const guardar = async () => {
    setError(null)
    setMensaje(null)
    const revisada = revisarTarifaPorDistancia({ precioBase, precioPorKm, radioMaximoKm })
    if (revisada.error) {
      setError({ mensaje: revisada.error.mensaje, campo: revisada.error.campo })
      return
    }
    setGuardando(true)
    const datos: Record<string, unknown> = { tipo: 'distancia', pais, ...revisada.datos, masAlla, plazo, gratisDesde: numeroDe(gratisDesde), activa, traducciones, origen: { direccion: direccion.trim() } }
    try {
      if (zona) await pedir(`/zonas-de-envio/${zona.id}`, { metodo: 'PATCH', cuerpo: datos })
      else await pedir('/zonas-de-envio', { metodo: 'POST', cuerpo: { ...datos, nombre: 'Envío por distancia', cliente: espacio, sitio, orden: 1000 } })
      setMensaje({ texto: direccion.trim() && direccionCambiada ? 'Guardado. La dirección de tu local queda ubicada en el mapa.' : 'Guardado.', tono: 'ok' })
      alCambiar()
    } catch (e) {
      setError(errorDeGuardado(e))
    } finally {
      setGuardando(false)
    }
  }

  /** «Ubicar»: el núcleo geocodifica la dirección y la guarda en la zona; aquí se enseña cómo la escribe Google. */
  const ubicar = () => {
    setUbicando(true)
    setMensaje(null)
    setError(null)
    pedir<{ ok: boolean; direccion: string }>('/zonas-de-envio/google/origen', { metodo: 'POST', cuerpo: { sitio, direccion: direccion.trim() } })
      .then((r) => {
        setDireccion(r.direccion)
        setMensaje({ texto: `Ubicada: ${r.direccion}. Desde ahí se miden los kilómetros.`, tono: 'ok' })
        alCambiar()
      })
      .catch((e: Error) => setError({ mensaje: e.message, campo: 'origen.direccion' }))
      .finally(() => setUbicando(false))
  }

  return (
    <Tarjeta>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-base font-semibold">
          <MapPin aria-hidden className="size-4 text-violeta" /> Envío por distancia con Google Maps <span className="text-[13px] font-normal text-tenue">(opcional)</span>
        </h2>
        {zona && <Pastilla clave={listo && zona.activa ? 'publicado' : 'sin-publicar'}>{listo ? (zona.activa ? 'En uso' : 'Desactivado') : 'Sin terminar'}</Pastilla>}
      </div>
      <p className="mb-4 text-[13px] text-tenue">
        El comprador escribe su dirección y la elige de una lista de sugerencias, y el envío se cobra por los kilómetros por carretera desde tu local: un precio base más un precio por cada kilómetro empezado. No hace falta ninguna clave ni cuenta de Google: las consultas van con la nuestra y las incluye tu plan. Si Google no responde, se le piden los desplegables de lugares y se cobra por tus zonas.
      </p>

      {!googleDisponible && (
        <div className="mb-4">
          <Aviso tono="aviso">Todavía no tenemos Google Maps configurado, así que el envío por distancia no se puede activar. Escríbenos y lo encendemos; mientras tanto, usa las zonas por lugares.</Aviso>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {error && !error.campo && <Aviso tono="error">{error.mensaje}</Aviso>}
        <label className="flex items-center gap-2 text-sm">
          <Casilla disabled={!googleDisponible} marcada={activa && googleDisponible} onChange={() => setActiva(!activa)} />
          Activar: el carrito ofrece escribir la dirección con sugerencias
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">País de tu local</span>
          <Selector onChange={(e) => setPais(e.target.value)} value={pais}>
            {paises.map((p) => (
              <option key={p.codigo} value={p.codigo}>
                {p.nombre}
              </option>
            ))}
          </Selector>
          <span className="text-xs text-tenue">Las sugerencias de dirección se limitan a este país.</span>
        </label>

        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[16rem] flex-1">
              <CampoDeTexto
                ayuda={zona ? (ubicada && !direccionCambiada ? 'Ubicada en el mapa: desde aquí se miden los kilómetros.' : 'Escríbela con calle, número y distrito, y pulsa «Ubicar» para comprobar cómo la entiende Google. Al guardar también se ubica.') : 'Escríbela con calle, número y distrito. Al guardar, la ubicamos en el mapa.'}
                cambiar={setDireccion}
                error={errorEn('origen.direccion')}
                etiqueta="Dirección de origen"
                placeholder="Av. Benavides 500, Miraflores"
                valor={direccion}
              />
            </div>
            {zona && (
              <Boton cargando={ubicando} disabled={!googleDisponible || direccion.trim().length < 5} icono={<MapPin />} onClick={ubicar} title="Una consulta a Google Maps, de las que incluye tu plan.">
                Ubicar
              </Boton>
            )}
          </div>
          {zona && ubicada && !direccionCambiada && <Pastilla clave="publicado">Ubicada</Pastilla>}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <CampoDeTexto ayuda={`En ${moneda}. Lo que se cobra siempre.`} cambiar={setPrecioBase} error={errorEn('precioBase')} etiqueta="Precio base" placeholder="8" tipo="decimal" valor={precioBase} />
          <CampoDeTexto ayuda="Por cada kilómetro empezado por carretera. 0 si solo cobras la base." cambiar={setPrecioPorKm} error={errorEn('precioPorKm')} etiqueta="Precio por kilómetro" placeholder="0,50" tipo="decimal" valor={precioPorKm} />
          <CampoDeTexto ayuda="Hasta dónde envías por distancia." cambiar={setRadioMaximoKm} error={errorEn('radioMaximoKm')} etiqueta="Radio máximo (km)" placeholder="15" tipo="decimal" valor={radioMaximoKm} />
        </div>
        {zona && <p className="text-[13px] text-tinta-2">Ahora mismo: {tarifaEnLlano(zona, moneda)}.</p>}

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Más lejos del radio</span>
          <Selector onChange={(e) => setMasAlla(e.target.value === 'zonas' ? 'zonas' : 'no-enviar')} value={masAlla}>
            <option value="no-enviar">No se envía (se le dice al comprador hasta dónde llegas)</option>
            <option value="zonas">Se cobra por mis zonas de lugares</option>
          </Selector>
          {masAlla === 'zonas' && zonasPorLugares === 0 && <span className="text-xs text-aviso">Todavía no tienes zonas por lugares: más allá del radio no se podrá enviar.</span>}
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <CampoDeTexto cambiar={setPlazo} error={errorEn('plazo')} etiqueta="Plazo de entrega" placeholder="En el día" valor={plazo} />
          <CampoDeTexto cambiar={setGratisDesde} error={errorEn('gratisDesde')} etiqueta="Envío gratis desde (opcional)" placeholder="200" tipo="decimal" valor={gratisDesde} />
        </div>
        <TraduccionesDeZona cambiar={setTraducciones} datos={{ nombre: zona?.nombre ?? null, plazo }} sitio={sitio} valor={traducciones} />

        {mensaje && <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>}
        <div className="flex flex-wrap gap-2">
          <Boton cargando={guardando} onClick={() => void guardar()} tipo="primario">
            {zona ? 'Guardar' : 'Activar el envío por distancia'}
          </Boton>
          {zona && (
            <Boton icono={<Trash2 />} onClick={() => setQuitando(true)} tipo="peligro">
              Quitar
            </Boton>
          )}
        </div>
      </div>

      {quitando && zona && (
        <Modal
          ancho="max-w-md"
          cerrar={() => setQuitando(false)}
          pie={
            <>
              <Boton onClick={() => setQuitando(false)}>Cancelar</Boton>
              <Boton
                icono={<Trash2 />}
                onClick={() => {
                  void pedir(`/zonas-de-envio/${zona.id}`, { metodo: 'DELETE' })
                    .then(() => {
                      setQuitando(false)
                      alCambiar()
                    })
                    .catch((e: Error) => setMensaje({ texto: e.message, tono: 'error' }))
                }}
                tipo="peligro"
              >
                Quitar
              </Boton>
            </>
          }
          titulo="¿Quitar el envío por distancia?"
        >
          <p className="text-sm">El carrito volverá a pedir el país y los lugares en desplegables, y se cobrará por tus zonas. Los pedidos ya hechos no cambian.</p>
        </Modal>
      )}
    </Tarjeta>
  )
}

/** «Uso de mapas este mes»: cuántas consultas a Google Maps lleva la web, la cuota del plan y qué pasa al llegar. Lo dice el núcleo. */
const UsoDeMapas = ({ sitio }: { sitio: number }) => {
  const { data, error, isPending } = useQuery({ queryKey: ['mapas-consumo', sitio], queryFn: () => pedir<UsoDeMapas>(`/entregas/mapas/consumo${consulta({ sitio })}`) })
  return (
    <Tarjeta>
      <h2 className="mb-1 flex items-center gap-2 font-display text-base font-semibold">
        <Gauge aria-hidden className="size-4 text-violeta" /> Uso de mapas este mes
      </h2>
      <p className="mb-3 text-[13px] text-tenue">Cada dirección que un comprador escribe y elige son dos o tres consultas a Google Maps; ubicar tu local y el mapa de tu web también cuentan. Las consultas las incluye tu plan.</p>
      {isPending ? (
        <Esqueleto filas={2} />
      ) : error || !data ? (
        <Aviso tono="error">{error instanceof ErrorDeLaApi ? error.message : 'No hemos podido leer el uso de mapas. Vuelve a intentarlo en un momento.'}</Aviso>
      ) : (
        <UsoDeMapasDetalle uso={data} />
      )}
    </Tarjeta>
  )
}

const UsoDeMapasDetalle = ({ uso }: { uso: UsoDeMapas }) => {
  const { resumen, alTope, tono } = usoDeMapasEnLlano(uso)
  const porcentaje = uso.porcentaje ?? 0
  const porTipo = (Object.keys(ETIQUETAS_DE_CONSUMO) as Array<keyof typeof ETIQUETAS_DE_CONSUMO>).filter((t) => (uso.porTipo[t]?.cantidad ?? 0) > 0)
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium">
        {mesEnLlano(uso.mes)}: {resumen}
      </p>
      {uso.cuota > 0 && (
        <div aria-label={`${porcentaje} % de la cuota de mapas`} aria-valuemax={100} aria-valuemin={0} aria-valuenow={porcentaje} className="h-2 w-full overflow-hidden rounded-full bg-papel-2" role="progressbar">
          <div className={`h-full rounded-full ${tono === 'error' ? 'bg-error' : tono === 'aviso' ? 'bg-aviso' : 'bg-violeta'}`} style={{ width: `${porcentaje}%` }} />
        </div>
      )}
      {porTipo.length > 0 && (
        <ul className="grid gap-1 text-[13px] text-tinta-2 sm:grid-cols-2">
          {porTipo.map((t) => (
            <li className="flex justify-between gap-2" key={t}>
              <span>{ETIQUETAS_DE_CONSUMO[t]}</span>
              <span className="tabular-nums">{uso.porTipo[t].cantidad}</span>
            </li>
          ))}
        </ul>
      )}
      <Aviso tono={tono}>{alTope}</Aviso>
    </div>
  )
}

/** Lo que el comprador lee de una zona —su nombre, su plazo, su horario— en los otros idiomas de la web (§37). */
const TraduccionesDeZona = ({ sitio, datos, valor, cambiar }: { sitio: number; datos: Record<string, unknown>; valor: ZonaEnPantalla['traducciones']; cambiar: (v: NonNullable<ZonaEnPantalla['traducciones']> | null) => void }) => {
  const { data: idiomas } = useIdiomas(sitio)
  if (!idiomas || idiomas.encendidos.length < 2) return null
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium">En los otros idiomas de tu web</span>
      <EditorDeTraducciones cambiar={cambiar} coleccion="zonas-de-envio" datos={datos} sitio={sitio} valor={valor} />
    </div>
  )
}
